import React, { useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import ReactPaginate from "react-paginate";
import "../HRManagement/leavePolicies.css";
import "./leavePortal.css";

function LeaveRequestsPanel({ userId, refreshKey = 0, compact = false }) {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: compact ? 5 : 10,
    total: 0,
    lastPage: 1,
  });

  useEffect(() => {
    if (userId) fetchLeaveRequests();
  }, [userId, pagination.currentPage, pagination.perPage, refreshKey]);

  const handlePageChange = (selectedItem) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: selectedItem.selected + 1,
    }));
  };

  const fetchLeaveRequests = async () => {
    setLoading(true);
    try {
      const response = await apiUrl.get(`/user/leave-requests/${userId}`, {
        params: {
          page: pagination.currentPage,
          perPage: pagination.perPage,
        },
      });
      if (response?.status) {
        setLeaveRequests(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination.total,
          lastPage: response.data.pagination.lastPage,
          currentPage: response.data.pagination.currentPage,
        }));
      }
    } catch (error) {
      console.error("Error fetching leave requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("en-GB");
  };

  const formatTime = (time) => {
    if (!time) return "—";
    return time.substring(0, 5);
  };

  const getStatusChip = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved") {
      return (
        <span className="lp-status-chip approved">
          <FaIcons.FaCheckCircle /> Approved
        </span>
      );
    }
    if (s === "rejected") {
      return (
        <span className="lp-status-chip rejected">
          <FaIcons.FaTimesCircle /> Rejected
        </span>
      );
    }
    return (
      <span className="lp-status-chip pending">
        <FaIcons.FaClock /> Pending
      </span>
    );
  };

  const getUnitDisplay = (unit) => {
    switch (unit) {
      case "full_day":
        return "Full Day";
      case "half_day":
        return "Half Day";
      case "short_leave":
        return "Short Leave";
      default:
        return unit;
    }
  };

  const filteredRequests = leaveRequests.filter((r) => {
    if (statusFilter === "all") return true;
    return (r.status || "").toLowerCase() === statusFilter;
  });

  const counts = {
    all: pagination.total || leaveRequests.length,
    pending: leaveRequests.filter((r) => r.status === "pending").length,
    approved: leaveRequests.filter((r) => r.status === "approved").length,
    rejected: leaveRequests.filter((r) => r.status === "rejected").length,
  };

  return (
    <div className="lp-section-card lp-table-card lp-requests-panel">
      <div className="lp-section-header d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div>
          <h6 className="fw-semibold mb-0">
            <FaIcons.FaClipboardList className="me-2 text-primary" />
            My Leave Requests
          </h6>
          <p className="text-muted small mb-0">
            {pagination.total} total request{pagination.total !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="lp-filter-tabs">
          {[
            { id: "all", label: "All" },
            { id: "pending", label: "Pending" },
            { id: "approved", label: "Approved" },
            { id: "rejected", label: "Rejected" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={statusFilter === tab.id ? "active" : ""}
              onClick={() => setStatusFilter(tab.id)}
            >
              {tab.label} ({counts[tab.id] ?? 0})
            </button>
          ))}
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead>
            <tr>
              <th>Leave Type</th>
              <th>Period</th>
              <th>Duration</th>
              <th>Time</th>
              <th>Status</th>
              <th>Applied</th>
              <th>HR Notes</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(compact ? 3 : 5)].map((_, i) => (
                <tr key={i} className="placeholder-glow">
                  <td><span className="placeholder col-8" /></td>
                  <td><span className="placeholder col-7" /></td>
                  <td><span className="placeholder col-5" /></td>
                  <td><span className="placeholder col-5" /></td>
                  <td><span className="placeholder col-4" /></td>
                  <td><span className="placeholder col-6" /></td>
                  <td><span className="placeholder col-8" /></td>
                </tr>
              ))
            ) : filteredRequests.length === 0 ? (
              <tr>
                <td colSpan="7">
                  <div className="lp-empty-state py-4">
                    <div className="empty-icon">
                      <FaIcons.FaCalendarAlt size={24} />
                    </div>
                    <h6 className="fw-semibold">No requests found</h6>
                    <p className="text-muted small mb-0">
                      {statusFilter === "all"
                        ? "You haven't submitted any leave requests yet."
                        : `No ${statusFilter} requests on this page.`}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredRequests.map((request) => (
                <tr key={request.id}>
                  <td>
                    <div className="fw-semibold">{request.leave_type_name}</div>
                    <small className="text-muted">{request.leave_type_code}</small>
                  </td>
                  <td>
                    <div>{formatDate(request.start_date)}</div>
                    <small className="text-muted">to {formatDate(request.end_date)}</small>
                  </td>
                  <td>
                    <span className="badge lp-badge-soft-secondary">
                      {getUnitDisplay(request.unit)}
                    </span>
                  </td>
                  <td>
                    {request.start_time || request.end_time ? (
                      <small>
                        {formatTime(request.start_time)} – {formatTime(request.end_time)}
                      </small>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    {getStatusChip(request.status)}
                    {request.status === "approved" && request.approved_by_name && (
                      <div className="text-muted small mt-1">by {request.approved_by_name}</div>
                    )}
                  </td>
                  <td>
                    <div>{formatDate(request.created_at)}</div>
                    <small className="text-muted">
                      {new Date(request.created_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </small>
                  </td>
                  <td>
                    {request.admin_notes ? (
                      <small className="text-muted">
                        <FaIcons.FaComment className="me-1" />
                        {request.admin_notes}
                      </small>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!compact && pagination.lastPage > 1 && (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top">
          <div className="text-muted small">
            Page {pagination.currentPage} of {pagination.lastPage}
          </div>
          <ReactPaginate
            previousLabel="Previous"
            nextLabel="Next"
            breakLabel="..."
            pageCount={pagination.lastPage}
            marginPagesDisplayed={1}
            pageRangeDisplayed={2}
            onPageChange={handlePageChange}
            forcePage={pagination.currentPage - 1}
            containerClassName="pagination mb-0 pagination-sm"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            activeClassName="active"
            disabledClassName="disabled"
          />
        </div>
      )}
    </div>
  );
}

export default LeaveRequestsPanel;
